import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import { isNil } from '@gfe/core/utils/shared-utils';

import { ICONS } from '@core/services/icons/icons.service';

import { AutocompleteComponent } from '@shared/components/autocomplete/autocomplete.component';
import { MultiAutocompleteComponent } from '@shared/components/autocomplete/multi-autocomplete.component';
import { ButtonComponent } from '@shared/components/button/button.component';
import { EntityAutoCompleteComponent } from '@shared/components/row-edition-form/entity-auto-complete/entity-auto-complete.component';
import { SelectItem } from '@shared/components/select/select-item.model';
import { SelectComponent } from '@shared/components/select/select.component';

import { FilterOperationsService } from '../filter-operations.service';
import {
  DateFilterCondition,
  DurationFilterCondition,
  FilterCondition,
  FilterDataType,
  FilterOperation,
  LabelFilterCondition,
  NumberFilterCondition,
  RelativeDateOption,
  SelectFilterCondition,
  TagFilterCondition,
  TextFilterCondition,
  UidFilterCondition
} from '../filter.models';

@Component({
  selector: 'app-filter-popup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    AutocompleteComponent,
    MultiAutocompleteComponent,
    EntityAutoCompleteComponent,
    SelectComponent,
    ButtonComponent
  ],
  templateUrl: './filter-popup.component.html'
})
export class FilterPopupComponent {
  protected filterOpsService = inject(FilterOperationsService);
  private translate = inject(TranslateService);

  // Inputs
  filterCondition = input.required<FilterCondition>();
  availableValues = input<string[]>([]); // For select types

  // Outputs
  updateFiltering = output<FilterCondition>();
  removeFiltering = output<void>();
  closePopup = output<void>();

  // Signals
  protected selectedOperation = signal<FilterOperation>(FilterOperation.EQUALS);
  protected localCondition = signal<FilterCondition | null>(null);

  // Form controls for entity auto-complete
  protected uidFormControl = new FormControl<string | null>(null);
  protected uidListFormControl = new FormControl<string[]>([]);

  protected readonly ICONS = ICONS;
  protected readonly FilterOperation = FilterOperation;

  protected readonly autoCompleteSeparators = [',', ';'];
  protected readonly emptyOptions: string[] = [];

  // Reactive language
  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  // Computed
  protected availableOperations = computed(() => {
    const condition = this.filterCondition();
    return this.filterOpsService.getOperationConfigsForDataType(condition.dataType);
  });

  protected selectedOperationConfig = computed(() =>
    this.filterOpsService.getOperationConfig(this.selectedOperation())
  );

  protected operationItems = computed<SelectItem[]>(() =>
    this.availableOperations().map((config) => ({
      id: config.operation,
      label: config.label,
      icon: config.icon
    }))
  );

  protected relativeDateItems = computed<SelectItem[]>(() =>
    this.relativeDateOptions().map((option) => ({
      id: option.value,
      label: option.label
    }))
  );

  protected showValueInput = computed(() => {
    const config = this.selectedOperationConfig();
    return config.requiresValue && !config.requiresTwoValues;
  });

  protected showTwoValueInputs = computed(() => {
    return this.selectedOperationConfig().requiresTwoValues || false;
  });

  protected showMultiSelect = computed(() => {
    const config = this.selectedOperationConfig();

    if (
      this.filterCondition().dataType === FilterDataType.MULTI_SELECT &&
      config.requiresValue &&
      !config.requiresTwoValues
    ) {
      return true;
    }

    return config.supportsMultipleValues || false;
  });

  protected isTextFilter = computed(() => {
    const dataType = this.filterCondition().dataType;
    return (
      dataType === FilterDataType.TEXT ||
      dataType === FilterDataType.MULTILINE_TEXT ||
      dataType === FilterDataType.LINK
    );
  });

  protected isNumberFilter = computed(() => {
    return this.filterCondition().dataType === FilterDataType.NUMBER;
  });

  protected isDateFilter = computed(() => {
    return this.filterCondition().dataType === FilterDataType.DATE;
  });

  protected isDurationFilter = computed(() => {
    return this.filterCondition().dataType === FilterDataType.DURATION;
  });

  protected isSelectFilter = computed(() => {
    const dataType = this.filterCondition().dataType;
    return dataType === FilterDataType.SELECT || dataType === FilterDataType.MULTI_SELECT;
  });

  protected isUidListFilter = computed(() => {
    return this.filterCondition().dataType === FilterDataType.UID_LIST;
  });

  protected isUidFilter = computed(() => {
    return this.filterCondition().dataType === FilterDataType.UID;
  });

  protected isUidFilterMultiSelect = computed(() => {
    if (!this.isUidFilter()) return false;
    const operation = this.selectedOperation();
    return (
      operation === FilterOperation.IS_ANY_OF || operation === FilterOperation.IS_NONE_OF
    );
  });

  protected isBooleanFilter = computed(() => {
    return this.filterCondition().dataType === FilterDataType.BOOLEAN;
  });

  protected isLabelsFilter = computed(() => {
    return this.filterCondition().dataType === FilterDataType.LABELS;
  });

  protected isLabelHasValueInput = computed(() => {
    if (!this.isLabelsFilter()) return false;
    const config = this.selectedOperationConfig();
    const labelNeed = config.labelNeed;
    return labelNeed === 'both' || labelNeed === 'value';
  });

  // An enum label exposes its declared variants via availableValues; the value
  // input is then constrained to that set instead of being free-text.
  protected isEnumLabelFilter = computed(() => {
    return this.isLabelsFilter() && this.availableValues().length > 0;
  });

  protected isLabelHasMultipleValues = computed(() => {
    if (!this.isLabelsFilter()) return false;
    const operation = this.selectedOperation();
    return (
      operation === FilterOperation.LABEL_IN || operation === FilterOperation.LABEL_NOT_IN
    );
  });

  protected isTagsFilter = computed(() => {
    return this.filterCondition().dataType === FilterDataType.TAGS;
  });

  protected relativeDateOptions = computed(() => {
    this.currentLang(); // Trigger reactivity
    return Object.values(RelativeDateOption).map((option) => ({
      value: option,
      label: this.translate.instant(`filters.relative_dates.${option.toLowerCase()}`)
    }));
  });

  constructor() {
    // Sync local condition with input
    effect(() => {
      const condition = this.filterCondition();
      this.localCondition.set(condition);
      this.selectedOperation.set(condition.operation);

      // Initialize form controls with current values
      if (this.isUidFilter()) {
        const uidCondition = condition as UidFilterCondition;
        const values = uidCondition.values || [];
        const value = uidCondition.value || null;

        // Multi-select for IS_ANY_OF / IS_NONE_OF
        if (
          condition.operation === FilterOperation.IS_ANY_OF ||
          condition.operation === FilterOperation.IS_NONE_OF
        ) {
          this.uidListFormControl.setValue(values, { emitEvent: false });
        } else {
          this.uidFormControl.setValue(value, { emitEvent: false });
        }
      } else if (this.isUidListFilter()) {
        const uidCondition = condition as UidFilterCondition;
        const values = uidCondition.values || [];
        this.uidListFormControl.setValue(values, { emitEvent: false });
      }
    });

    // Listen to UID form control changes
    this.uidFormControl.valueChanges.subscribe((value) => {
      if (value !== null && value !== undefined) {
        this.updateUidValue(value);
      }
    });

    // Listen to UID list form control changes
    this.uidListFormControl.valueChanges.subscribe((uids) => {
      if (uids !== null && uids !== undefined) {
        if (this.isUidFilter()) {
          this.updateUidValues(uids);
        } else if (this.isUidListFilter()) {
          this.updateUidsFromAutoComplete(uids);
        }
      }
    });
  }

  protected selectOperation(operation: FilterOperation) {
    this.selectedOperation.set(operation);

    const currentCondition = this.getCondition();

    let updated: FilterCondition = {
      ...currentCondition,
      operation
    };

    if (this.isUidFilter()) {
      updated = this.transitionUidCondition(
        currentCondition as UidFilterCondition,
        operation
      ) as FilterCondition;
    } else if (this.isUidListFilter()) {
      const uidCondition = currentCondition as UidFilterCondition;
      updated = this.isNoValueOperation(operation)
        ? { ...uidCondition, operation, values: undefined }
        : { ...uidCondition, operation };
    } else if (this.isLabelsFilter()) {
      const labelCondition = currentCondition as LabelFilterCondition;
      if (
        operation === FilterOperation.LABEL_IN ||
        operation === FilterOperation.LABEL_NOT_IN
      ) {
        updated = {
          ...labelCondition,
          operation,
          labelValues: labelCondition.labelValues || [],
          labelValue: undefined
        } as LabelFilterCondition;
      } else if (
        operation === FilterOperation.LABEL_EQUALS ||
        operation === FilterOperation.LABEL_NOT_EQUALS
      ) {
        updated = {
          ...labelCondition,
          operation,
          labelValue: labelCondition.labelValue || '',
          labelValues: undefined
        } as LabelFilterCondition;
      } else {
        updated = {
          ...labelCondition,
          operation,
          labelValue: undefined,
          labelValues: undefined
        } as LabelFilterCondition;
      }
    }

    this.localCondition.set(updated);
    this.syncFormControlsToCondition(updated);
  }

  private isUidMultiSelectOperation(operation: FilterOperation): boolean {
    return (
      operation === FilterOperation.IS_ANY_OF || operation === FilterOperation.IS_NONE_OF
    );
  }

  private isNoValueOperation(operation: FilterOperation): boolean {
    return (
      operation === FilterOperation.IS_EMPTY || operation === FilterOperation.IS_NOT_EMPTY
    );
  }

  private transitionUidCondition(
    current: UidFilterCondition,
    operation: FilterOperation
  ): UidFilterCondition {
    if (this.isNoValueOperation(operation)) {
      return { ...current, operation, value: undefined, values: undefined };
    }

    const wasMulti = this.isUidMultiSelectOperation(current.operation);
    const willBeMulti = this.isUidMultiSelectOperation(operation);

    if (!wasMulti && willBeMulti) {
      // Single → List: promote the single value into the list
      const values = current.value ? [current.value] : current.values || [];
      return { ...current, operation, value: undefined, values };
    }

    if (wasMulti && !willBeMulti) {
      // List → Single: use the first item from the list
      const firstValue = (current.values || [])[0] ?? undefined;
      return { ...current, operation, value: firstValue, values: undefined };
    }

    // Same mode (single → single or list → list): keep existing values
    return { ...current, operation };
  }

  private syncFormControlsToCondition(condition: FilterCondition): void {
    if (this.isUidFilter()) {
      const uidCondition = condition as UidFilterCondition;
      if (this.isUidMultiSelectOperation(condition.operation)) {
        this.uidListFormControl.setValue(uidCondition.values || [], { emitEvent: false });
      } else {
        this.uidFormControl.setValue(uidCondition.value || null, { emitEvent: false });
      }
    } else if (this.isUidListFilter()) {
      const uidCondition = condition as UidFilterCondition;
      this.uidListFormControl.setValue(uidCondition.values || [], { emitEvent: false });
    }
  }

  private getCondition(): FilterCondition {
    return this.localCondition()!;
  }

  protected updateTextValue(value: string) {
    const condition = this.getCondition() as TextFilterCondition;
    this.localCondition.set({
      ...condition,
      value
    });
  }

  protected updateLabelSingleValue(value: string) {
    const condition = this.getCondition() as LabelFilterCondition;
    this.localCondition.set({
      ...condition,
      labelValue: value,
      labelValues: undefined
    });
  }

  protected updateLabelMultipleValues(values: string[]) {
    const condition = this.getCondition() as LabelFilterCondition;
    this.localCondition.set({
      ...condition,
      labelValue: undefined,
      labelValues: values
    });
  }

  protected updateUidValue(value: string) {
    const condition = this.getCondition() as UidFilterCondition;
    this.localCondition.set({
      ...condition,
      value,
      values: undefined
    });
  }

  protected updateUidValues(values: string[]) {
    const condition = this.getCondition() as UidFilterCondition;
    this.localCondition.set({
      ...condition,
      value: undefined,
      values
    });
  }

  protected updateNumberValue(value: number) {
    const condition = this.getCondition() as NumberFilterCondition;
    this.localCondition.set({
      ...condition,
      value
    });
  }

  protected updateNumberMinValue(value: number) {
    const condition = this.getCondition() as NumberFilterCondition;
    this.localCondition.set({
      ...condition,
      minValue: value
    });
  }

  protected updateNumberMaxValue(value: number) {
    const condition = this.getCondition() as NumberFilterCondition;
    this.localCondition.set({
      ...condition,
      maxValue: value
    });
  }

  protected updateDateValue(value: string) {
    const condition = this.getCondition() as DateFilterCondition;
    this.localCondition.set({
      ...condition,
      value
    });
  }

  protected updateDateMinValue(value: string) {
    const condition = this.getCondition() as DateFilterCondition;
    this.localCondition.set({
      ...condition,
      minValue: value
    });
  }

  protected updateDateMaxValue(value: string) {
    const condition = this.getCondition() as DateFilterCondition;
    this.localCondition.set({
      ...condition,
      maxValue: value
    });
  }

  protected updateRelativeDate(value: RelativeDateOption) {
    const condition = this.getCondition() as DateFilterCondition;
    this.localCondition.set({
      ...condition,
      relativeDate: value
    });
  }

  protected updateDurationValue(value: string) {
    const condition = this.getCondition() as DurationFilterCondition;
    this.localCondition.set({
      ...condition,
      value
    });
  }

  protected updateDurationMinValue(value: string) {
    const condition = this.getCondition() as DurationFilterCondition;
    this.localCondition.set({
      ...condition,
      minValue: value
    });
  }

  protected updateDurationMaxValue(value: string) {
    const condition = this.getCondition() as DurationFilterCondition;
    this.localCondition.set({
      ...condition,
      maxValue: value
    });
  }

  protected toggleSelectValue(value: string) {
    const condition = this.getCondition() as SelectFilterCondition;
    const currentValues = condition.values || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];

    this.localCondition.set({
      ...condition,
      values: newValues
    });
  }

  protected setSelectValue(value: string) {
    const condition = this.getCondition() as SelectFilterCondition;
    this.localCondition.set({
      ...condition,
      values: [value]
    });
  }

  protected applyFilter() {
    this.close();
    const condition = this.localCondition();
    if (condition) {
      const enabledCondition = { ...condition, enabled: true };
      this.updateFiltering.emit(enabledCondition);
    }
  }

  protected removeFilter() {
    this.close();
    this.removeFiltering.emit();
  }

  protected close() {
    this.closePopup.emit();
  }

  protected getTextValue(): string {
    const condition = this.getCondition() as TextFilterCondition;
    return condition.value || '';
  }

  protected getNumberValue(): number | undefined {
    const condition = this.getCondition() as NumberFilterCondition;
    return condition.value;
  }

  protected getNumberMinValue(): number | undefined {
    const condition = this.getCondition() as NumberFilterCondition;
    return condition.minValue;
  }

  protected getNumberMaxValue(): number | undefined {
    const condition = this.getCondition() as NumberFilterCondition;
    return condition.maxValue;
  }

  protected getDateValue(): string {
    const condition = this.getCondition() as DateFilterCondition;
    return condition.value || '';
  }

  protected getDateMinValue(): string {
    const condition = this.getCondition() as DateFilterCondition;
    return condition.minValue || '';
  }

  protected getDateMaxValue(): string {
    const condition = this.getCondition() as DateFilterCondition;
    return condition.maxValue || '';
  }

  protected getRelativeDate(): RelativeDateOption | undefined {
    const condition = this.getCondition() as DateFilterCondition;
    return condition.relativeDate;
  }

  protected getDurationValue(): string {
    const condition = this.getCondition() as DurationFilterCondition;
    return condition.value || '';
  }

  protected getDurationMinValue(): string {
    const condition = this.getCondition() as DurationFilterCondition;
    return condition.minValue || '';
  }

  protected getDurationMaxValue(): string {
    const condition = this.getCondition() as DurationFilterCondition;
    return condition.maxValue || '';
  }

  // Memoized so the reference stays stable across change-detection passes.
  // A method returning a fresh `[]` each call (e.g. via [ngModel]) makes NgModel
  // detect a change every CD pass, reschedule, and loop forever (app freeze).
  protected selectValues = computed<string[]>(() => {
    const condition = this.localCondition() as SelectFilterCondition | null;
    return condition?.values ?? [];
  });

  protected tagValues = computed<string[]>(() => {
    const condition = this.localCondition() as TagFilterCondition | null;
    return condition?.tagNames ?? [];
  });

  protected labelValues = computed<string[]>(() => {
    const condition = this.localCondition() as LabelFilterCondition | null;
    return condition?.labelValues ?? [];
  });

  protected getLabelValue(): string {
    const condition = this.getCondition() as LabelFilterCondition;
    return condition.labelValue || '';
  }

  protected isValueSelected(value: string): boolean {
    return this.selectValues().includes(value);
  }

  protected toggleUidValue(uid: string) {
    const condition = this.getCondition() as UidFilterCondition;
    const currentValues = condition.values || [];
    const newValues = currentValues.includes(uid)
      ? currentValues.filter((u) => u !== uid)
      : [...currentValues, uid];

    this.localCondition.set({
      ...condition,
      values: newValues
    });
  }

  protected updateUidsFromText(value: string) {
    const condition = this.getCondition() as UidFilterCondition;
    // Split by comma, trim whitespace, filter empty strings
    const values = value
      .split(',')
      .map((uid) => uid.trim())
      .filter((uid) => uid.length > 0);

    this.localCondition.set({
      ...condition,
      values
    });
  }

  /**
   * Picking a value to filter by is a different task from picking one to store, so the
   * filter popup's pickers keep their own saved filters rather than the field's.
   */
  protected readonly FILTER_PRESET_SURFACE = 'FILTER';

  /** DB column the condition targets, which scopes that picker's saved filters. */
  protected filteredField(): string {
    return this.getCondition().field;
  }

  protected getUidEntityType(): string {
    const condition = this.getCondition() as UidFilterCondition;
    return condition.entityType || '';
  }

  protected getUids(): string[] {
    const condition = this.getCondition() as UidFilterCondition;
    return condition.values || [];
  }

  protected getUidsAsText(): string {
    return this.getUids().join(', ');
  }

  protected isUidSelected(uid: string): boolean {
    return this.getUids().includes(uid);
  }

  protected updateUidsFromAutoComplete(uids: string[]) {
    const condition = this.getCondition() as UidFilterCondition;
    this.localCondition.set({
      ...condition,
      values: uids
    });
  }

  protected updateConditionValuesFromAutoComplete(
    values: string[],
    key: string = 'values'
  ) {
    const condition = this.getCondition();
    this.localCondition.set({
      ...condition,
      [key]: values
    });
  }

  protected getUidValue(): string | null {
    const condition = this.getCondition() as UidFilterCondition;
    return condition.value || null;
  }
}
